// CRITICAL: Import console override FIRST before any other code that might use console
// This ensures all console.log/warn/info/debug calls are silenced in production
import './utils/console-override';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MantineProvider, createTheme } from '@mantine/core';

import App from './App';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import './styles/globals.css';

// Validate environment configuration on startup
import { validateEnvironment, getEnvironmentInfo } from './config/environment';

// Import utility for manual Apify sync (available in console as syncApifyRun)
import './utils/syncApifyRun';

// Import Georgia recall debugging utility (available in console as debugGeorgiaRecalls)
import './utils/debugGeorgiaRecalls';

// Import zone-recall mapping debugging utility (available in console as debugZoneRecallMapping)
import './utils/debugZoneRecallMapping';

// Import cache clearing utilities (available in console)
import { setQueryClientInstance } from './utils/clearZoneMapCaches';
import './utils/clearZoneMapCaches';

// Import materialized view debugging utility (available in console as debugMaterializedView)
import './utils/debugMaterializedView';

// Import Georgia query debugging utility (available in console as debugGeorgiaQuery)
import './utils/debugGeorgiaQuery';

// Import Georgia cluster generation debugging utility (available in console as debugGeorgiaClusterGeneration)
import './utils/debugGeorgiaClusterGeneration';

// Import FL vs GA comparison utility (available in console as compareFLvsGA)
import './utils/compareFLvsGA';

// Import GA base table debugging utility (available in console as debugGAInBaseTable)
import './utils/debugGAInBaseTable';

// Import GA prod schema debugging utility (available in console as debugGAProdSchema)
import './utils/debugGAProdSchema';

try {
  validateEnvironment();
} catch (error) {
  console.error('❌ Environment validation failed:', error);

  // Render configuration error page instead of the app
  const rootElement = document.getElementById('root');
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <div
          style={{
            padding: '2rem',
            maxWidth: '800px',
            margin: '2rem auto',
            fontFamily: 'system-ui, sans-serif',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px'
          }}
        >
          <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Configuration Error</h1>
          <p style={{ marginBottom: '1rem' }}>
            The application failed to start due to missing or invalid environment configuration:
          </p>
          <pre
            style={{
              backgroundColor: '#f3f4f6',
              padding: '1rem',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '0.875rem'
            }}
          >
            {error instanceof Error ? error.message : String(error)}
          </pre>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
            Please check your <code>.env.local</code> file and ensure all required environment
            variables are set. You can use <code>.env.example</code> as a template.
          </p>
        </div>
      </React.StrictMode>
    );
  }
  throw error; // Re-throw to prevent further execution
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error) {
          // Check for 403/Forbidden errors in message
          if (
            error.message?.includes('403') ||
            error.message?.includes('Forbidden') ||
            error.message?.includes('permission denied') ||
            error.message?.includes('DIRECT_QUERY_NOT_ALLOWED') ||
            error.message?.includes('DIRECT_COUNT_QUERY_NOT_ALLOWED')
          ) {
            return false;
          }
          // Check for status code if available
          if ('status' in error && typeof error.status === 'number') {
            if (error.status >= 400 && error.status < 500) {
              return false;
            }
          }
        }
        return failureCount < 3;
      }
    },
    mutations: {
      retry: false
    }
  }
});

// Mantine theme configured to match brand colors
const mantineTheme = createTheme({
  primaryColor: 'brand',
  defaultRadius: 'md',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  colors: {
    brand: [
      '#fef7ee', // lightest
      '#fde6d0',
      '#fcd5b1',
      '#fac392',
      '#f9b273',
      '#d4854a', // brand-primary-500 (main brand color)
      '#b6703f',
      '#985b34',
      '#7a4629',
      '#5c311f' // darkest
    ]
  },
  components: {
    DatePickerInput: {
      defaultProps: {
        size: 'md'
      }
    }
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

// Make query client available for cache utilities
setQueryClientInstance(queryClient);

// Hydrate sessions for both main and prod clients before app renders
// This ensures both clients have loaded sessions from localStorage before any queries
import { hydrateSessions } from './lib/supabase/client';

// Await hydration so the prod client has the authenticated session before any
// React component fires a query. Uses a timeout so we never block the render
// indefinitely (e.g. when a token refresh takes too long).
const HYDRATION_TIMEOUT_MS = 3000;

async function initApp() {
  try {
    await Promise.race([
      hydrateSessions(),
      new Promise(resolve => setTimeout(resolve, HYDRATION_TIMEOUT_MS))
    ]);
  } catch (error) {
    console.error('Failed to hydrate sessions:', error);
  }

  // rootElement is guaranteed non-null by the throw on line 160
  ReactDOM.createRoot(rootElement as HTMLElement).render(
    <React.StrictMode>
      <MantineProvider theme={mantineTheme}>
        <QueryClientProvider client={queryClient}>
          <App />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </MantineProvider>
    </React.StrictMode>
  );
}

initApp();
